import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MapPin, Save, ExternalLink, RefreshCw, Copy, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  adminGetGoogleLocalFeed,
  adminPutGoogleLocalFeed,
  adminGoogleLocalFeedHealth,
} from "../../lib/api";

/**
 * Admin → Google Local Feeds (iter47w).
 *
 * Two TSV feeds for the Ready-Made catalogue:
 *   1. Free Listings  — /api/google/ready-made-products.tsv
 *   2. Local Inventory — /api/google/ready-made-local-inventory.tsv (one row
 *      per product per physical store, linked to Google Business Profile).
 *
 * Admin can:
 *   - Set the Toronto store code (must match Merchant Center → Local
 *     inventory → Stores).
 *   - Override default brand, pickup_method, pickup_SLA, google category.
 *   - View a health report (how many products are valid, why others are
 *     excluded) and copy the live feed URLs.
 */

const PUBLIC_BASE = "https://torontocustomtshirts.ca";

export default function GoogleLocalFeedTab({ pw }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        adminGetGoogleLocalFeed(pw),
        adminGoogleLocalFeedHealth(pw),
      ]);
      setSettings(s);
      setHealth(h);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Google Local Feed settings");
    }
  }, [pw]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!settings) return;
    setSaving(true);
    try {
      const r = await adminPutGoogleLocalFeed(pw, {
        store_code: settings.store_code,
        default_brand: settings.default_brand,
        default_pickup_method: settings.default_pickup_method,
        default_pickup_sla: settings.default_pickup_sla,
        default_google_category: settings.default_google_category,
      });
      setSettings(r);
      toast.success("Local Feed settings saved — feeds refresh on next Google fetch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const refreshHealth = async () => {
    setLoadingHealth(true);
    try {
      const h = await adminGoogleLocalFeedHealth(pw);
      setHealth(h);
      toast.success("Health refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    } finally {
      setLoadingHealth(false);
    }
  };

  const productsUrl = `${PUBLIC_BASE}/api/google/ready-made-products.tsv`;
  const localUrl = `${PUBLIC_BASE}/api/google/ready-made-local-inventory.tsv`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  if (!settings) {
    return <div className="font-mono text-xs text-zinc-500">LOADING…</div>;
  }

  const set = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6" data-testid="google-local-feed-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <MapPin size={22} className="text-[var(--tc-red)]" /> Google Local Feeds (Ready-Made)
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Two TSV feeds for Google Merchant Center → Free Listings + Local Inventory. Updates push to Google on every scheduled fetch.
        </p>
      </div>

      {/* Feed URLs */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-emerald-900 bg-emerald-950/20 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg uppercase tracking-tight">Free Listings Feed</h3>
            {health && (
              <span className="font-mono text-[10px] tracking-widest text-emerald-300" data-testid="free-listings-count">
                {health.free_listings_count} PRODUCTS
              </span>
            )}
          </div>
          <div className="border border-[#333] bg-black p-3 font-mono text-[11px] text-emerald-300 break-all" data-testid="free-listings-url">
            {productsUrl}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => copy(productsUrl, "Free Listings URL")}
              data-testid="copy-free-listings-url-btn"
              className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1.5"
            >
              <Copy size={12} /> COPY URL
            </button>
            <a
              href={productsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="open-free-listings-btn"
              className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1.5"
            >
              <ExternalLink size={12} /> OPEN
            </a>
          </div>
        </div>

        <div className="border border-amber-900 bg-amber-950/20 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg uppercase tracking-tight">Local Inventory Feed</h3>
            {health && (
              <span className="font-mono text-[10px] tracking-widest text-amber-300" data-testid="local-inventory-count">
                {health.local_inventory_count} PRODUCTS
              </span>
            )}
          </div>
          <div className="border border-[#333] bg-black p-3 font-mono text-[11px] text-amber-300 break-all" data-testid="local-inventory-url">
            {localUrl}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => copy(localUrl, "Local Inventory URL")}
              data-testid="copy-local-inventory-url-btn"
              className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1.5"
            >
              <Copy size={12} /> COPY URL
            </button>
            <a
              href={localUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="open-local-inventory-btn"
              className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1.5"
            >
              <ExternalLink size={12} /> OPEN
            </a>
          </div>
        </div>
      </div>

      {/* Settings editor */}
      <form onSubmit={handleSave} className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4">
        <h3 className="font-display text-lg uppercase tracking-tight">Feed Defaults</h3>
        <p className="font-mono text-[11px] text-zinc-500 -mt-2">
          These values apply to every ready-made product unless individually overridden on the listing editor.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">
              STORE CODE (Google Business Profile)
            </span>
            <input
              type="text"
              value={settings.store_code}
              onChange={(e) => set("store_code", e.target.value)}
              data-testid="store-code-input"
              placeholder="toronto-main"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
            />
            <span className="font-mono text-[10px] text-zinc-500">Must match Merchant Center → Local inventory → Stores.</span>
          </label>

          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">DEFAULT BRAND</span>
            <input
              type="text"
              value={settings.default_brand}
              onChange={(e) => set("default_brand", e.target.value)}
              data-testid="default-brand-input"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 focus:border-[var(--tc-red)] focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">DEFAULT PICKUP METHOD</span>
            <select
              value={settings.default_pickup_method}
              onChange={(e) => set("default_pickup_method", e.target.value)}
              data-testid="default-pickup-method-select"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 focus:border-[var(--tc-red)] focus:outline-none"
            >
              {(settings.valid_pickup_methods || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">DEFAULT PICKUP SLA</span>
            <select
              value={settings.default_pickup_sla}
              onChange={(e) => set("default_pickup_sla", e.target.value)}
              data-testid="default-pickup-sla-select"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 focus:border-[var(--tc-red)] focus:outline-none"
            >
              {(settings.valid_pickup_slas || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="block md:col-span-2">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">
              DEFAULT GOOGLE PRODUCT CATEGORY (taxonomy ID)
            </span>
            <input
              type="text"
              value={settings.default_google_category}
              onChange={(e) => set("default_google_category", e.target.value)}
              data-testid="default-google-category-input"
              placeholder="212"
              className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 focus:border-[var(--tc-red)] focus:outline-none"
            />
            <span className="font-mono text-[10px] text-zinc-500">
              212 = Apparel &amp; Accessories &gt; Clothing. Lookup:{" "}
              <a
                href="https://support.google.com/merchants/answer/6324436"
                target="_blank"
                rel="noreferrer"
                className="text-[var(--tc-red)] hover:underline"
              >Google taxonomy</a>
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="save-google-local-feed-btn"
          className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save size={14} /> {saving ? "SAVING…" : "SAVE DEFAULTS"}
        </button>
      </form>

      {/* Health */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg uppercase tracking-tight">Feed Health</h3>
          <button
            type="button"
            onClick={refreshHealth}
            disabled={loadingHealth}
            data-testid="refresh-health-btn"
            className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1.5"
          >
            <RefreshCw size={12} className={loadingHealth ? "animate-spin" : ""} /> REFRESH
          </button>
        </div>
        {health ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-[#222] bg-black p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">FREE LISTINGS</div>
                <div className="font-display text-3xl text-emerald-300">{health.free_listings_count}</div>
              </div>
              <div className="border border-[#222] bg-black p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">LOCAL INVENTORY</div>
                <div className="font-display text-3xl text-amber-300">{health.local_inventory_count}</div>
              </div>
              <div className="border border-[#222] bg-black p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">TOTAL PRODUCTS</div>
                <div className="font-display text-3xl text-zinc-200">{health.total_products}</div>
              </div>
            </div>
            {health.excluded_total > 0 ? (
              <div className="border border-amber-900 bg-amber-950/20 p-3 mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={14} className="text-amber-400" />
                  <span className="font-mono text-[11px] tracking-widest text-amber-200">
                    {health.excluded_total} EXCLUDED FROM FEEDS
                  </span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {health.excluded.map((x) => (
                    <div key={`${x.id}-${x.feed}`} className="flex justify-between font-mono text-[11px] text-zinc-400">
                      <span className="truncate">
                        <span className="text-zinc-600">[{x.feed}]</span> {x.name}
                      </span>
                      <span className="text-amber-300 ml-3 shrink-0">{x.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="border border-emerald-900 bg-emerald-950/20 p-3 mt-2 flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-400" />
                <span className="font-mono text-[11px] tracking-widest text-emerald-200">
                  ALL PRODUCTS QUALIFY FOR BOTH FEEDS
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="font-mono text-xs text-zinc-500">Loading health…</div>
        )}
      </div>

      {/* Setup walkthrough */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Setup walkthrough</h3>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-300">
          <li>
            Confirm your Toronto store is registered in{" "}
            <a href="https://business.google.com" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline">Google Business Profile</a>.
          </li>
          <li>
            In <a href="https://merchants.google.com" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline">Merchant Center</a>, go to{" "}
            <strong>Local inventory → Stores</strong> and either link your Business Profile or upload a stores TSV. Note the <strong>store code</strong> Google assigns (or set your own — must match the value above).
          </li>
          <li>
            Add a <strong>Products</strong> data source: Merchant Center → Products → Data sources → Scheduled fetch → paste the <em>Free Listings</em> URL above.
          </li>
          <li>
            Add a <strong>Local inventory</strong> data source: Local inventory → Data sources → Scheduled fetch → paste the <em>Local Inventory</em> URL above.
          </li>
          <li>
            Set both to refresh <strong>Daily</strong>. Allow 24-48h for review. Once approved, your products appear in Google Maps + free local listings.
          </li>
        </ol>
      </div>
    </div>
  );
}
